import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { currentUser, userRole, signOut } = useAuth();
  const location = useLocation();

  const publicNav = [
    { id: 'home', label: 'Home', href: '/' },
    { id: 'roster', label: 'Team', href: '/roster' },
    { id: 'stats', label: 'Stats', href: '/stats' },
    { id: 'schedule', label: 'Schedule', href: '/schedule' },
    { id: 'about', label: 'About', href: '/about' },
    { id: 'contact', label: 'Join', href: '/contact' },
  ];

  const isActive = (href: string) => location.pathname === href;
  const dashboardHref = userRole === 'coach' ? '/coach/dashboard' : '/player/dashboard';

  return (
    <nav className="bg-willard-black sticky top-0 z-40 border-b border-willard-grey-800">
      <div className="max-w-6xl mx-auto px-5">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src="/assets/logos/W-logo.png" alt="Willard W" className="h-9 invert" />
            <div className="leading-tight">
              <p className="font-display text-white text-sm tracking-wide">WILLARD TIGERS</p>
              <p className="text-[10px] font-bold text-willard-taupe uppercase tracking-[0.2em]">Bowling</p>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {publicNav.map((tab) => (
              <Link
                key={tab.id}
                to={tab.href}
                className={`px-3.5 py-2 rounded-lg font-bold text-sm transition-colors ${
                  isActive(tab.href)
                    ? 'text-willard-black bg-willard-taupe'
                    : 'text-willard-grey-300 hover:text-white hover:bg-willard-grey-800'
                }`}
              >
                {tab.label}
              </Link>
            ))}
            {currentUser ? (
              <div className="relative ml-2">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="px-3.5 py-2 border border-willard-grey-700 text-white rounded-lg font-bold text-sm hover:border-willard-taupe transition-colors flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  {currentUser.displayName?.split(' ')[0] || 'Account'}
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-willard-grey-900 border border-willard-grey-700 rounded-xl py-2 shadow-tiger-2xl">
                    <Link
                      to={dashboardHref}
                      className="block px-5 py-2.5 font-bold text-willard-grey-200 hover:bg-willard-grey-800 transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={async () => {
                        await signOut();
                        setUserMenuOpen(false);
                      }}
                      className="w-full text-left px-5 py-2.5 font-bold text-willard-grey-200 hover:bg-willard-grey-800 transition-colors flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/signin"
                className="ml-2 px-4 py-2 border border-willard-grey-700 text-white rounded-lg font-bold text-sm hover:border-willard-taupe transition-colors"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-willard-grey-800 bg-willard-black px-5 py-3 space-y-1">
          {publicNav.map((tab) => (
            <Link
              key={tab.id}
              to={tab.href}
              onClick={() => setMobileOpen(false)}
              className={`block px-4 py-3 rounded-lg font-bold ${
                isActive(tab.href)
                  ? 'bg-willard-taupe text-willard-black'
                  : 'text-willard-grey-200 hover:bg-willard-grey-800'
              }`}
            >
              {tab.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-willard-grey-800">
            {currentUser ? (
              <>
                <Link
                  to={dashboardHref}
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 rounded-lg font-bold text-willard-grey-200 hover:bg-willard-grey-800"
                >
                  Dashboard
                </Link>
                <button
                  onClick={async () => {
                    await signOut();
                    setMobileOpen(false);
                  }}
                  className="w-full text-left px-4 py-3 rounded-lg font-bold text-willard-grey-200 hover:bg-willard-grey-800"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/signin"
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-3 rounded-lg font-bold text-willard-grey-200 hover:bg-willard-grey-800"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
